import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {BootstrapColors} from '@src/@types/Bootstrap';
import RenderIf from '@src/components/RenderIf/RenderIf';
import classnames from '@src/helpers/classnames';
import UserAvatar from '@src/components/UserAvatar/UserAvatar';
import {UserAvatarProps} from '@src/@types/components/UserAvatar';
import Translate from '@src/components/Translate/Translate';
import {INotificationProps} from '@src/@interfaces/components/Notifications/INotificationProps';
import {getDuration, stringifyDate} from '@src/helpers/dates';
import {getEndpointUri} from '@src/helpers/getEndpointUri';
import app from '@src/helpers/app';
import queryClient from '@src/react-query-client';
import {getMessagesQueryKey} from '../_layouts/Default/components/content/notifications/Messages';
import {INotificationModel} from '@src/@interfaces/models/Notification/INotificationModel';
import {markNotificationAsRead} from '@src/_redux/notifications/actions';
import {DateTime} from '@src/@types/DateTime';
import ErrorRepresentation from '@src/components/ErrorRepresentation/ErrorRepresentation';

type State = {
    loading: boolean,
    markedAsRead: boolean,
    errorMessage: { name: string, message: string } | null,
};

function NotificationAge({createdAt}: { createdAt?: DateTime | null }) {
    const [updatedAt, setUpdatedAt] = useState(0);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!createdAt) {
            return;
        }

        if (timer.current) {
            clearTimeout(timer.current);
        }

        const timeout = 60 * 1000;
        timer.current = setTimeout(function tick() {
            setUpdatedAt(Date.now());

            timer.current = setTimeout(tick, timeout);
        }, timeout);

        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        };
    }, [updatedAt, createdAt, timer]);

    if (!createdAt) {
        return null;
    }

    return <div className="notifications__item__age">
        <i className="fas fa-clock-rotate-left me-1"/>
        {getDuration(createdAt).humanize(true)}
    </div>;
}

export default abstract class AbstractNotification<
        P extends INotificationProps = INotificationProps
> extends React.Component<P, State> {
    state: State = {
        loading: false,
        markedAsRead: false,
        errorMessage: null,
    };

    constructor(props: P) {
        super(props);

        this.onMarkAsReadClicked = this.onMarkAsReadClicked.bind(this);
    }

    render() {
        const Tag = this.getWrapperTag();
        const {className, ...props} = this.getWrapperTagProps();

        return <Tag {...props} className={classnames('notifications__item', className, {
            'notifications__item--marked-as-read': this.props.read_at != null || this.state.markedAsRead,
        })}>
            <div className="notifications__item__wrapper">
                {this.renderBody()}
            </div>
        </Tag>;
    }

    protected abstract renderAvatar(): ReactNode;

    protected renderControls() {
        if (this.props.read_at != null || this.state.markedAsRead) {
            return null;
        }

        return <div className="notifications__item__controls">
            <button
                    type="button"
                    className="notifications__item__mark-as-read-control"
                    disabled={this.state.loading}
                    onClick={this.onMarkAsReadClicked}
            >
                <i className={classnames('fa', {
                    'fa-spin fa-spinner': this.state.loading,
                    'fa-envelope-open': !this.state.loading,
                })}/>
            </button>
        </div>;
    }

    protected renderBody(): ReactNode {
        return <>
            {this.renderControls()}

            <div className="notifications__item__header">
                {this.renderAvatar()}

                {this.renderTitle()}
            </div>

            {this.state.errorMessage ? (
                    <ErrorRepresentation
                            error={this.state.errorMessage}
                            className="mt-2 px-2 py-1"
                            outsideContainer
                            withoutErrorName
                    />
            ) : null}

            <div className="notifications__item__body">
                {this.renderMessage()}

                {this.renderAge()}
            </div>
        </>;
    }

    protected renderTitle(): ReactNode {
        return <Translate
                message={this.getTitle()}
                replace={this.getTitleReplacements()}
                choice={this.getTitleChoiceValue()}
                withComponentsAsReplacements
                tag="h6"
                className="notifications__item__title"
        />;
    }

    protected renderMessage(): ReactNode {
        return <RenderIf
                expression={this.getDescription()}
                render={() => {
                    return <Translate
                            message={this.getDescription() as string}
                            replace={this.getDescriptionReplacements()}
                            choice={this.getDescriptionChoiceValue()}
                            withComponentsAsReplacements
                            tag="p"
                            className="mb-1"
                    />;
                }}
        />;
    }

    protected renderAge(): ReactNode {
        return <NotificationAge createdAt={this.props.created_at}/>;
    }

    protected renderIconAvatar(icon: string | ReactNode, color?: BootstrapColors): ReactNode {
        return <div className="notifications__item__icon">
            <span className={classnames({
                [`bg-${color}`]: Boolean(color),
            })}>
                <RenderIf
                        expression={typeof icon === 'string'}
                        render={() => <i className={icon as string}/>}
                        renderElse={() => icon}
                />
            </span>
        </div>;
    }

    protected renderUserAvatar(user: UserAvatarProps['user']): ReactNode {
        return <UserAvatar<UserAvatarProps & React.HTMLAttributes<HTMLElement>>
                user={user}
                className="me-3 rounded-circle avatar-xs flex-shrink-0"
        />;
    }

    protected onMarkAsReadClicked(e: React.MouseEvent) {
        e.stopPropagation();
        e.preventDefault();

        if (this.props.read_at != null) {
            return;
        }

        this.setState({loading: true, errorMessage: null});
        app('http').route('profile.notifications.mark-as-read', {
            id: String(this.props.id),
        }).then(() => {
            const key = getMessagesQueryKey();

            queryClient.setQueriesData<INotificationModel<any>[]>({queryKey: key, exact: false}, data => {
                const index = data?.findIndex(i => i.id === this.props.id);
                if (!data || index == null || index < 0) {
                    return data;
                }

                const newList = [...data];
                newList[index].read_at = stringifyDate();

                return newList;
            });
            queryClient.invalidateQueries(key, {exact: false, refetchType: 'none'}).then();

            app().dispatchAction(markNotificationAsRead(String(this.props.id)));
            this.setState({markedAsRead: true});
        }).catch(e => {
            this.setState({
                errorMessage: {
                    name: e?.name || 'HTTPError',
                    message: e?.message || 'Something went wrong',
                }
            });
        }).finally(() => {
            this.setState({loading: false});
        });
    }

    protected getWrapperTag() {
        return Boolean(this.getWrapperTagProps().to) ? Link : 'div';
    }

    protected getWrapperTagProps() {
        const props: any = {};
        if (this.props.uri) {
            props.to = getEndpointUri(this.props.uri, '#');
        }

        ['role', 'tabIndex'].forEach(key => {
            if (key in this.props) {
                props[key] = (this.props as any)[key];
            }
        });

        return props;
    }

    protected getTitle() {
        return this.props.title;
    }

    protected getDescription() {
        return this.props.description;
    }

    protected getChoiceValue(): number {
        return 1;
    }

    protected getTitleChoiceValue(): number {
        return this.getChoiceValue();
    }

    protected getDescriptionChoiceValue(): number {
        return this.getChoiceValue();
    }

    protected getTitleReplacements() {
        return {};
    }

    protected getDescriptionReplacements() {
        return {};
    }
}
